import type { AppConfig } from './config';
import { buildAuthorizeUrl, buildLogoutUrl, cognitoOrigin, codeChallenge, parseCallback, randomString, tokenRequestBody } from './pkce';

export interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** epoch ms */
  expiresAt: number;
}

export const SESSION_KEY = 'mona.auth.session';
export const PENDING_KEY = 'mona.auth.pending';
const REFRESH_MARGIN_MS = 60_000;

interface Pending {
  verifier: string;
  state: string;
  returnTo: string;
}

let memory: TokenSet | null = null;
let refreshing: Promise<TokenSet | null> | null = null;

export function loadSession(): TokenSet | null {
  if (memory) return memory;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Partial<TokenSet>;
    if (typeof t.idToken === 'string' && typeof t.expiresAt === 'number') {
      memory = { idToken: t.idToken, accessToken: t.accessToken ?? '', refreshToken: t.refreshToken, expiresAt: t.expiresAt };
      return memory;
    }
  } catch {
    /* fall through */
  }
  return null;
}

function saveSession(t: TokenSet | null): void {
  memory = t;
  try {
    if (t) sessionStorage.setItem(SESSION_KEY, JSON.stringify(t));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* in-memory only */
  }
}

export const clearSession = (): void => saveSession(null);

interface TokenResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

async function tokenRequest(cfg: AppConfig, params: Record<string, string>): Promise<TokenResponse> {
  const res = await fetch(`${cognitoOrigin(cfg.cognitoDomain)}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: tokenRequestBody({ client_id: cfg.userPoolClientId, ...params }),
  });
  if (!res.ok) throw new Error(`Token endpoint answered ${res.status}`);
  return (await res.json()) as TokenResponse;
}

function toTokenSet(r: TokenResponse, previous?: TokenSet | null): TokenSet {
  if (!r.id_token) throw new Error('Token response had no id_token');
  return {
    idToken: r.id_token,
    accessToken: r.access_token ?? '',
    refreshToken: r.refresh_token ?? previous?.refreshToken,
    expiresAt: Date.now() + (r.expires_in ?? 3600) * 1000,
  };
}

/** Redirect to the Cognito Hosted UI. `returnTo` (path + query) is restored after the callback. */
export async function startLogin(cfg: AppConfig, returnTo: string): Promise<void> {
  const verifier = randomString(48);
  const state = randomString(16);
  const pending: Pending = { verifier, state, returnTo };
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    /* state would then fail validation; surfaced on callback */
  }
  const url = buildAuthorizeUrl(cfg.cognitoDomain, {
    clientId: cfg.userPoolClientId,
    redirectUri: `${window.location.origin}/auth/callback`,
    challenge: await codeChallenge(verifier),
    state,
  });
  window.location.assign(url);
}

/** Handles /auth/callback: validates state, exchanges the code, stores tokens. Returns the path to resume at. */
export async function completeLogin(cfg: AppConfig, search: string): Promise<string> {
  let pending: Pending | null = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null') as Pending | null;
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    pending = null;
  }
  const parsed = parseCallback(search, pending?.state ?? null);
  if (!parsed.ok) throw new Error(parsed.reason);
  const response = await tokenRequest(cfg, {
    grant_type: 'authorization_code',
    code: parsed.code,
    redirect_uri: `${window.location.origin}/auth/callback`,
    code_verifier: pending!.verifier,
  });
  saveSession(toTokenSet(response));
  const back = pending!.returnTo;
  // Never bounce back into the callback route itself.
  return back.startsWith('/auth/callback') || !back.startsWith('/') ? '/' : back;
}

export async function refreshSession(cfg: AppConfig): Promise<TokenSet | null> {
  const current = loadSession();
  if (!current?.refreshToken) return null;
  refreshing ??= (async () => {
    try {
      const r = await tokenRequest(cfg, { grant_type: 'refresh_token', refresh_token: current.refreshToken! });
      const next = toTokenSet(r, current);
      saveSession(next);
      return next;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** ID token valid for at least a minute; refreshes transparently. Null when a login is needed. */
export async function getValidIdToken(cfg: AppConfig): Promise<string | null> {
  const t = loadSession();
  if (!t) return null;
  if (t.expiresAt - REFRESH_MARGIN_MS > Date.now()) return t.idToken;
  return (await refreshSession(cfg))?.idToken ?? null;
}

export function signOut(cfg: AppConfig): void {
  clearSession();
  window.location.assign(buildLogoutUrl(cfg.cognitoDomain, cfg.userPoolClientId, `${window.location.origin}/signed-out`));
}
